import type { AiComponentTypeType } from '@terra/shared-schemas-orchestrator';

export const AI_SIGNALS_ASSET_KIND_PREFIX = 'ai-kind:';

export const AI_SIGNALS_ASSET_KIND_PROFILES = ['MCP_SERVER', 'AGENT'] as const;

export type AiSignalsAssetKindProfile =
  (typeof AI_SIGNALS_ASSET_KIND_PROFILES)[number];

export const AI_SIGNALS_ASSET_KINDS = [
  'all',
  'application',
  'MCP_TOOL',
  'MCP_PROMPT',
  'MCP_RESOURCE',
  'TOOL',
  'MODEL',
  'RAG',
  'MEMORY',
  'GUARDRAIL',
  'INTEGRATION',
] as const;

export type AiSignalsAssetKind = (typeof AI_SIGNALS_ASSET_KINDS)[number];

type AiComponentRef = {
  id: string;
  type: AiComponentTypeType;
};

const MCP_APP_WIDE_COMPONENT_TYPES = [
  'MCP_SERVER',
] as const satisfies readonly AiComponentTypeType[];

const AGENT_APP_WIDE_COMPONENT_TYPES = [
  'AGENT',
  'SUB_AGENT',
] as const satisfies readonly AiComponentTypeType[];

const COMPONENT_TYPES_BY_KIND: Record<
  AiSignalsAssetKindProfile,
  Partial<
    Record<Exclude<AiSignalsAssetKind, 'all'>, readonly AiComponentTypeType[]>
  >
> = {
  MCP_SERVER: {
    application: MCP_APP_WIDE_COMPONENT_TYPES,
    MCP_TOOL: ['MCP_TOOL'],
    MCP_PROMPT: ['MCP_PROMPT'],
    MCP_RESOURCE: ['MCP_RESOURCE'],
  },
  AGENT: {
    application: AGENT_APP_WIDE_COMPONENT_TYPES,
    TOOL: ['TOOL'],
    MODEL: ['MODEL'],
    RAG: ['RAG'],
    MEMORY: ['MEMORY'],
    GUARDRAIL: ['GUARDRAIL'],
    INTEGRATION: ['INTEGRATION'],
  },
};

export const AI_SIGNALS_ASSET_KIND_NAV_BY_PROFILE: Record<
  AiSignalsAssetKindProfile,
  readonly Exclude<AiSignalsAssetKind, never>[]
> = {
  MCP_SERVER: ['all', 'application', 'MCP_TOOL', 'MCP_PROMPT', 'MCP_RESOURCE'],
  AGENT: [
    'all',
    'application',
    'TOOL',
    'MODEL',
    'RAG',
    'MEMORY',
    'GUARDRAIL',
    'INTEGRATION',
  ],
};

export const resolveAiSignalsAssetKindProfile = ({
  applicationKind,
}: {
  applicationKind?: string | null;
}): AiSignalsAssetKindProfile =>
  applicationKind === 'AGENT' ? 'AGENT' : 'MCP_SERVER';

export const isAiSignalsAssetKindForProfile = (
  kind: string,
  profile: AiSignalsAssetKindProfile,
): kind is AiSignalsAssetKind =>
  (AI_SIGNALS_ASSET_KIND_NAV_BY_PROFILE[profile] as readonly string[]).includes(
    kind,
  );

export const toAiSignalsAssetKindSelectionId = (
  kind: AiSignalsAssetKind,
): string => `${AI_SIGNALS_ASSET_KIND_PREFIX}${kind}`;

export const parseAiSignalsAssetKindSelectionId = (
  selectionId: string,
): AiSignalsAssetKind | null => {
  if (!selectionId.startsWith(AI_SIGNALS_ASSET_KIND_PREFIX)) {
    return null;
  }

  const kind = selectionId.slice(AI_SIGNALS_ASSET_KIND_PREFIX.length);
  if ((AI_SIGNALS_ASSET_KINDS as readonly string[]).includes(kind)) {
    return kind as AiSignalsAssetKind;
  }

  return null;
};

export const resolveSelectedAiSignalsAssetKind = ({
  selectedDomains,
  profile,
}: {
  selectedDomains: string[];
  profile: AiSignalsAssetKindProfile;
}): AiSignalsAssetKind => {
  if (selectedDomains.length !== 1) {
    return 'all';
  }

  const kind = parseAiSignalsAssetKindSelectionId(selectedDomains[0]);
  if (!kind || !isAiSignalsAssetKindForProfile(kind, profile)) {
    return 'all';
  }

  return kind;
};

export const resolveAiSignalsAssetKindFilter = ({
  kind,
  profile,
}: {
  kind: AiSignalsAssetKind;
  profile: AiSignalsAssetKindProfile;
}): {
  componentTypes?: AiComponentTypeType[];
} => {
  if (kind === 'all') {
    return { componentTypes: undefined };
  }

  if (!isAiSignalsAssetKindForProfile(kind, profile)) {
    return { componentTypes: [] };
  }

  const matchingTypes = COMPONENT_TYPES_BY_KIND[profile][kind] ?? [];

  return {
    componentTypes: [...matchingTypes],
  };
};

export const countSignalsByAiAssetKind = ({
  components,
  countsByAssetId,
  profile,
}: {
  components: AiComponentRef[];
  countsByAssetId: Record<string, number>;
  profile: AiSignalsAssetKindProfile;
}): Record<AiSignalsAssetKind, number> => {
  const sumForTypes = (types: readonly string[]) =>
    components
      .filter((component) => types.includes(component.type))
      .reduce(
        (sum, component) => sum + (countsByAssetId[component.id] ?? 0),
        0,
      );

  const emptyCounts = Object.fromEntries(
    AI_SIGNALS_ASSET_KINDS.map((kind) => [kind, 0]),
  ) as Record<AiSignalsAssetKind, number>;

  const all = Object.values(countsByAssetId).reduce(
    (sum, count) => sum + count,
    0,
  );

  const kindsForProfile = AI_SIGNALS_ASSET_KIND_NAV_BY_PROFILE[profile];
  const profileCounts = { ...emptyCounts, all };

  for (const kind of kindsForProfile) {
    if (kind === 'all') {
      continue;
    }
    const types = COMPONENT_TYPES_BY_KIND[profile][kind] ?? [];
    profileCounts[kind] = sumForTypes(types);
  }

  return profileCounts;
};
